import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getHealthColor } from './ScoreIndicator';
import './SchoolInsights.css';

interface CategoryScore {
  percentPositive: number;
  averageScore: number;
}

interface SchoolData {
  schoolName: string;
  totalResponses: number;
  overallPercentPositive: number;
  overallAverageScore: number;
  healthIndicator: 'green' | 'yellow' | 'red';
  categories: Record<string, CategoryScore>;
  alerts: string[];
}

interface DistrictAverages {
  overallPercentPositive: number;
  categories: Record<string, number>;
}

interface UnifiedDistrictAverages {
  districtAverages: Record<string, number>;
  totalResponses: number;
  categoryNames: Record<string, string>;
}

type ComparisonMode = 'survey-type' | 'district-wide';

interface SchoolInsightsProps {
  surveyType: string;
  respondentType?: string;
}

type SortField = 'schoolName' | 'overallPercentPositive' | 'totalResponses';
type SortDirection = 'asc' | 'desc';

const surveyTypeLabels: Record<string, string> = {
  'k-2': 'K-2',
  '3-5': 'Grades 3-5',
  '6-8': 'Grades 6-8',
  'high-school': 'High School',
  'virtual-k8': 'Virtual K-8',
  'virtual-hs': 'Virtual HS',
  'staff-climate': 'Staff Climate',
};

const SchoolInsights: React.FC<SchoolInsightsProps> = ({ surveyType, respondentType = 'student' }) => {
  const { token } = useAuth();
  const [schools, setSchools] = useState<SchoolData[]>([]);
  const [districtAverages, setDistrictAverages] = useState<DistrictAverages | null>(null);
  const [unifiedDistrictAverages, setUnifiedDistrictAverages] = useState<UnifiedDistrictAverages | null>(null);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('district-wide');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('overallPercentPositive');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);

  // Fetch school data and survey-type-specific district averages
  useEffect(() => {
    const fetchData = async () => {
      if (!token || !surveyType) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/admin/stats/schools/breakdown?surveyType=${surveyType}&role=${respondentType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch school data');
        }

        const data = await response.json();
        setSchools(data.schools || []);
        setDistrictAverages(data.districtAverages || null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, surveyType, respondentType]);

  // Fetch unified district-wide averages (across all survey types)
  useEffect(() => {
    const fetchUnifiedData = async () => {
      if (!token) return;

      try {
        const response = await fetch(
          `/api/admin/stats/district-unified?role=${respondentType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          console.error('Failed to fetch unified district data');
          return;
        }

        const data = await response.json();
        setUnifiedDistrictAverages(data);
      } catch (err: any) {
        console.error('Error fetching unified district data:', err);
      }
    };

    fetchUnifiedData();
  }, [token, respondentType]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedSchools = [...schools].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortField) {
      case 'schoolName':
        aVal = a.schoolName.toLowerCase();
        bVal = b.schoolName.toLowerCase();
        break;
      case 'overallPercentPositive':
        aVal = a.overallPercentPositive;
        bVal = b.overallPercentPositive;
        break;
      case 'totalResponses':
        aVal = a.totalResponses;
        bVal = b.totalResponses;
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const categoryIds = schools.length > 0 ? Object.keys(schools[0].categories) : [];

  // Get the effective district average for a category based on comparison mode
  const getDistrictAverage = (categoryId: string): number => {
    if (comparisonMode === 'district-wide' && unifiedDistrictAverages) {
      return unifiedDistrictAverages.districtAverages[categoryId] || 0;
    }
    return districtAverages?.categories[categoryId] || 0;
  };

  // Get the overall effective district average
  const getOverallDistrictAverage = (): number => {
    if (comparisonMode === 'district-wide' && unifiedDistrictAverages) {
      // Calculate average of all unified categories
      const values = Object.values(unifiedDistrictAverages.districtAverages);
      return values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
    }
    return districtAverages?.overallPercentPositive || 0;
  };

  const selectedSchoolData = selectedSchool
    ? schools.find((s) => s.schoolName === selectedSchool)
    : null;

  if (isLoading) {
    return (
      <div className="school-insights__loading">
        <div className="school-insights__spinner" />
        <p>Loading school insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="school-insights__error">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="school-insights__empty">
        <p>No school data available for this survey type.</p>
      </div>
    );
  }

  return (
    <div className="school-insights">
      <div className="school-insights__header">
        <div className="school-insights__header-left">
          <h2>School Comparison - {surveyTypeLabels[surveyType] || surveyType}</h2>
          <div className="school-insights__district-avg">
            {comparisonMode === 'district-wide' ? 'District-Wide' : surveyTypeLabels[surveyType]} Average:{' '}
            <strong style={{ color: getHealthColor(getOverallDistrictAverage()) }}>
              {getOverallDistrictAverage()}% Positive
            </strong>
          </div>
        </div>
        <div className="school-insights__comparison-toggle">
          <span className="comparison-toggle__label">Compare to:</span>
          <div className="comparison-toggle__buttons">
            <button
              className={`comparison-toggle__btn ${comparisonMode === 'survey-type' ? 'active' : ''}`}
              onClick={() => setComparisonMode('survey-type')}
            >
              {surveyTypeLabels[surveyType] || surveyType} Only
            </button>
            <button
              className={`comparison-toggle__btn ${comparisonMode === 'district-wide' ? 'active' : ''}`}
              onClick={() => setComparisonMode('district-wide')}
            >
              All Students
            </button>
          </div>
        </div>
      </div>

      <div className="school-insights__table-wrapper">
        <table className="school-insights__table">
          <thead>
            <tr>
              <th
                className={`sortable ${sortField === 'schoolName' ? 'sorted' : ''}`}
                onClick={() => handleSort('schoolName')}
              >
                School
                {sortField === 'schoolName' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th
                className={`sortable ${sortField === 'totalResponses' ? 'sorted' : ''}`}
                onClick={() => handleSort('totalResponses')}
              >
                Responses
                {sortField === 'totalResponses' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              <th
                className={`sortable ${sortField === 'overallPercentPositive' ? 'sorted' : ''}`}
                onClick={() => handleSort('overallPercentPositive')}
              >
                Overall
                {sortField === 'overallPercentPositive' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
              {categoryIds.map((catId) => (
                <th key={catId} className="category-col">
                  {catId.charAt(0).toUpperCase() + catId.slice(1)}
                </th>
              ))}
              <th>Alerts</th>
            </tr>
          </thead>
          <tbody>
            {sortedSchools.map((school) => (
              <tr
                key={school.schoolName}
                className={selectedSchool === school.schoolName ? 'selected' : ''}
                onClick={() => setSelectedSchool(school.schoolName)}
              >
                <td className="school-name-cell">{school.schoolName}</td>
                <td className="numeric-cell">~{school.totalResponses}</td>
                <td className="score-cell">
                  <span
                    className="score-value"
                    style={{ color: getHealthColor(school.overallPercentPositive) }}
                  >
                    {Math.round(school.overallPercentPositive)}%
                  </span>
                </td>
                {categoryIds.map((catId) => {
                  const catScore = school.categories[catId];
                  const districtCatScore = getDistrictAverage(catId);
                  const diff = catScore ? catScore.percentPositive - districtCatScore : 0;
                  return (
                    <td key={catId} className="score-cell category-score-cell">
                      {catScore ? (
                        <span
                          className="score-value"
                          style={{ color: getHealthColor(catScore.percentPositive) }}
                        >
                          {Math.round(catScore.percentPositive)}%
                          {diff <= -10 && (
                            <span className="diff-indicator negative" title={`${Math.round(diff)}% vs ${comparisonMode === 'district-wide' ? 'district' : surveyType}`}>
                              ↓
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                  );
                })}
                <td className="alerts-cell">
                  {school.alerts.length > 0 && (
                    <span className="alert-badge" title={school.alerts.join('\n')}>
                      {school.alerts.length}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedSchoolData && (
        <div className="school-insights__detail">
          <div className="school-insights__detail-header">
            <h3>{selectedSchoolData.schoolName}</h3>
            <button
              className="school-insights__close-btn"
              onClick={() => setSelectedSchool(null)}
            >
              Close
            </button>
          </div>
          <div className="school-insights__detail-content">
            <div className="school-insights__detail-stats">
              <div className="detail-stat">
                <span className="detail-stat-value">{Math.round(selectedSchoolData.overallPercentPositive)}%</span>
                <span className="detail-stat-label">Overall Positive</span>
              </div>
              <div className="detail-stat">
                <span className="detail-stat-value">{selectedSchoolData.overallAverageScore.toFixed(1)}</span>
                <span className="detail-stat-label">Average Score</span>
              </div>
              <div className="detail-stat">
                <span className="detail-stat-value">~{selectedSchoolData.totalResponses}</span>
                <span className="detail-stat-label">Responses</span>
              </div>
            </div>

            {selectedSchoolData.alerts.length > 0 && (
              <div className="school-insights__alerts">
                <h4>Alerts</h4>
                <ul>
                  {selectedSchoolData.alerts.map((alert, i) => (
                    <li key={i}>{alert}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="school-insights__category-comparison">
              <h4>Category Comparison vs {comparisonMode === 'district-wide' ? 'District-Wide' : surveyTypeLabels[surveyType]}</h4>
              <div className="category-bars">
                {categoryIds.map((catId) => {
                  const schoolScore = selectedSchoolData.categories[catId]?.percentPositive || 0;
                  const districtScore = getDistrictAverage(catId);
                  const schoolBeatsDistrict = schoolScore > districtScore;
                  const comparisonLabel = comparisonMode === 'district-wide'
                    ? 'District-Wide Average'
                    : `${surveyTypeLabels[surveyType]} Average`;
                  return (
                    <div key={catId} className="category-bar-row">
                      <span className="category-bar-label">{catId}</span>
                      <div className="category-bar-container">
                        {!schoolBeatsDistrict && (
                          <div
                            className="category-bar district"
                            style={{ width: `${districtScore}%` }}
                            data-tooltip={`${comparisonLabel}: ${Math.round(districtScore)}%`}
                          >
                            <span className="district-bar-value">{Math.round(districtScore)}%</span>
                          </div>
                        )}
                        <div
                          className="category-bar school"
                          style={{
                            width: `${schoolScore}%`,
                            backgroundColor: getHealthColor(schoolScore),
                          }}
                          data-tooltip={`${selectedSchoolData.schoolName}: ${Math.round(schoolScore)}%`}
                        />
                        {schoolBeatsDistrict && (
                          <div
                            className="district-overlay"
                            style={{ width: `${districtScore}%` }}
                            data-tooltip={`${comparisonLabel}: ${Math.round(districtScore)}%`}
                          >
                            <span className="district-overlay-value">{Math.round(districtScore)}%</span>
                          </div>
                        )}
                      </div>
                      <span className="category-bar-value">{Math.round(schoolScore)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolInsights;
